import React, { useMemo } from 'react';
import { CardHeader } from 'reactstrap';
import Skeleton from 'react-loading-skeleton';
import domain from 'src/config/domain';

const ConversionFunnel = ({ data, loading }) => {
  const domainConfig = domain();

  const stages = useMemo(() => {
    if (!data?.transactions?.status?.weekly) return [];

    const statusMap = {};
    for (const item of data.transactions.status.weekly) {
      statusMap[item.status] = (statusMap[item.status] || 0) + item.count;
    }

    let linksGenerated = 0;
    if (data?.payment_links?.status?.weekly) {
      for (const item of data.payment_links.status.weekly) {
        linksGenerated += item.count;
      }
    }

    const initiated = Object.values(statusMap).reduce((sum, count) => sum + count, 0);
    const abandoned = statusMap['ABANDONED'] || 0;
    const pending = statusMap['PENDING'] || 0;
    const declined = statusMap['DECLINED'] || 0;
    const processed = initiated - abandoned - pending;
    const accepted = processed - declined;

    const stages = [
      { label: 'Links Created', count: linksGenerated },
      { label: 'Processed', count: processed },
      { label: 'Completed', count: accepted },
    ];

    return stages.filter(stage => stage.count > 0 || stage.label === 'Links Generated');
  }, [data]);

  const maxCount = stages.length > 0 ? stages[0].count : 0;

  if (loading) {
    return (
      <div className="flex flex-col rounded-md border animate-in fade-in-50 mb-2">
        <CardHeader className="bg-gray-50 mb-2">
          <h2 className={`text-xl ${domainConfig.styles.textColour}`}>
            Payment <span className="font-bold">Conversion</span>
          </h2>
        </CardHeader>
        <div className="p-4">
          <Skeleton count={4} height="48px" className="mb-2" />
        </div>
      </div>
    );
  }

  if (maxCount === 0) {
    return (
      <div className="flex flex-col rounded-md border animate-in fade-in-50 mb-2">
        <CardHeader className="bg-gray-50 mb-2">
          <div className="float-right text-sm pt-0.5">
            <span className={`inline-flex items-center gap-x-1.5 py-1.5 px-3 rounded-md text-xs font-medium border ${domainConfig.styles.borderColour} ${domainConfig.styles.lightBGColour} ${domainConfig.styles.textColour}`}>
              Last 7 Days
            </span>
          </div>
          <h2 className={`text-xl ${domainConfig.styles.textColour}`}>
            Payment <span className="font-bold">Conversion</span>
          </h2>
        </CardHeader>
        <div className="text-center py-8 text-slate-400 text-sm">
          No transaction data available
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col rounded-md border animate-in fade-in-50 mb-2ca">
      <CardHeader className="bg-gray-50 mb-2">
        <div className="float-right text-sm pt-0.5">
          <span className={`inline-flex items-center gap-x-1.5 py-1.5 px-3 rounded-md text-xs font-medium border ${domainConfig.styles.borderColour} ${domainConfig.styles.lightBGColour} ${domainConfig.styles.textColour}`}>
            Last 7 Days
          </span>
        </div>
        <h2 className={`text-xl ${domainConfig.styles.textColour}`}>
          Payment <span className="font-bold">Conversion</span>
        </h2>
      </CardHeader>

      <div className="px-4 pb-6 pt-1">
        {stages.map((stage, index) => {
          const widthPercent = maxCount > 0 ? Math.max((stage.count / maxCount) * 100, 20) : 20;
          const conversionRate = index > 0 && stages[index - 1].count > 0
            ? ((stage.count / stages[index - 1].count) * 100).toFixed(1)
            : null;
          const overallRate = index > 0 && maxCount > 0
            ? ((stage.count / maxCount) * 100).toFixed(1)
            : null;

          const opacity = 1 - (index * 0.06);

          return (
            <div key={stage.label} className="mb-1.5 last:mb-0">
              {index > 0 && conversionRate && (
                <div className="flex items-center py-0.5 pl-3 mb-1">
                  <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ${domainConfig.styles.lightBGColour}`}>
                    <svg width="10" height="10" viewBox="0 0 10 10" className={`${domainConfig.styles.textColour} opacity-50 flex-shrink-0`}>
                      <path d="M5 1 L5 7 L2.5 4.5 M5 7 L7.5 4.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                    </svg>
                    <span className={`text-xs font-medium ${domainConfig.styles.textColour}`}>
                      {conversionRate}%
                    </span>
                  </div>
                </div>
              )}

              <div className="relative flex items-center">
                <div
                  className="relative h-12 rounded-md flex items-center px-4 transition-all duration-500"
                  style={{
                    width: `${widthPercent}%`,
                    backgroundColor: domainConfig.isAcquirerFlow
                      ? `rgba(30, 58, 138, ${opacity})`
                      : `rgba(88, 28, 135, ${opacity})`,
                    minWidth: '140px',
                  }}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="text-white text-sm font-medium truncate font-mono">
                      {stage.label}
                    </span>
                    <span className="text-white text-sm font-bold ml-2 whitespace-nowrap font-mono">
                      {new Intl.NumberFormat('en-GB').format(stage.count)}
                    </span>
                  </div>
                </div>

                {overallRate && (
                  <span className={`text-xs font-medium ml-3 whitespace-nowrap ${domainConfig.styles.textColour} opacity-60`}>
                    {overallRate}% of total
                  </span>
                )}
              </div>
            </div>
          );
        })}

        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>
              Overall conversion:
              <span className={`font-bold ml-1 ${domainConfig.styles.textColour}`}>
                {maxCount > 0 ? ((stages[stages.length - 1].count / maxCount) * 100).toFixed(1) : 0}%
              </span>
            </span>
            <span className="text-slate-400">
              {stages.length > 1 ? `${new Intl.NumberFormat('en-GB').format(maxCount - stages[stages.length - 1].count)} dropped off` : ''}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversionFunnel;
